/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import Divider from '@mui/material/Divider';
import { alpha, useTheme } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { bgGradient } from '../../theme/css';
import Logo from '../components/Logo';
import { useDispatch, useSelector } from 'react-redux';
import { auth, grantToken, login } from '@ink-ai/portal/reducers/auth';
import { RootState, getStore } from '@ink-ai/portal/reducers';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { secureRandomString } from '@ink-ai/portal/common/utils';
import { OIDC_SCOPE } from '@ink-ai/portal/common/authHelper';

// Keep the original message handling function for backward compatibility with popup window login
const receiveCode = (event: MessageEvent) => {
  if (event.origin !== window.location.origin) {
    return;
  }
  const data = event.data;
  if (data?.source !== 'ink-ai-oidc') {
    return;
  }
  const store = getStore();
  console.log('Received secret key:', data.code);
  store.dispatch(
    grantToken({
      grant_type: 'authorization_code',
      code: data.code,
    }),
  );
};

window.addEventListener('message', receiveCode, false);

export const Login = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch<any>();
  const authState = useSelector((state: RootState) => state.auth);
  const appState = useSelector((state: RootState) => state.app);
  const location = useLocation();
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);

  // Added: Save original navigation state
  useEffect(() => {
    // When component loads, if there's a 'from' path but no 'code' parameter, save the path to sessionStorage
    if (location.state?.from?.pathname && !location.search.includes('code=')) {
      sessionStorage.setItem(
        'ink_ai_auth_redirect_path',
        location.state.from.pathname,
      );
    }
  }, []);

  // Added: Extract authorization code and validate state parameter from URL query parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (code) {
      // Validate state parameter to prevent CSRF attacks
      const savedState = sessionStorage.getItem('ink_ai_auth_state');

      if (state && savedState && state === savedState) {
        console.log('State parameter validated successfully');
        console.log('Received authorization code from URL:', code);

        // Clear the saved state as it's no longer needed
        sessionStorage.removeItem('ink_ai_auth_state');
        const codeVerifier = sessionStorage.getItem('ink_ai_pkce_verifier') || undefined;
        sessionStorage.removeItem('ink_ai_pkce_verifier');

        dispatch(
          grantToken({
            grant_type: 'authorization_code',
            code: code,
            code_verifier: codeVerifier,
          }),
        );

        // Clear authorization code from URL to prevent reprocessing on page refresh
        navigate(location.pathname, { replace: true });
      } else {
        console.error(
          'State parameter validation failed - possible CSRF attack',
        );
        // Handle the error - could show an error message to the user
        dispatch(auth.actions.changeLoading(false));
      }
    }
  }, [location.search]);

  // Generate a random string for PKCE code_verifier
  const generateCodeVerifier = (): string => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
  };

  // Generate code_challenge from code_verifier using SHA-256
  const generateCodeChallenge = async (verifier: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  };

  const onLoginClick = async () => {
    dispatch(auth.actions.changeLoading(true));

    const stateParam = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem('ink_ai_auth_state', stateParam);

    // PKCE
    const codeVerifier = generateCodeVerifier();
    sessionStorage.setItem('ink_ai_pkce_verifier', codeVerifier);
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    const oidcPage = `${appState.appParam.authorization_endpoint}?scope=${encodeURIComponent(OIDC_SCOPE)}&response_type=code&client_id=${appState.appParam.cognito_client_id}&redirect_uri=${appState.addInHost}/oidc.html&state=${stateParam}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
    console.log('Redirecting to cognito page: ', oidcPage);

    window.location.href = oidcPage;
  };

  useEffect(() => {
    if (!authState.idToken) {
      return;
    }
    if (
      authState.expiresAt &&
      authState.expiresAt * 1000 <= Date.now() &&
      authState.refreshToken
    ) {
      console.log('access token expired, refreshing token');
      dispatch(
        grantToken({
          grant_type: 'refresh_token',
          refresh_token: authState.refreshToken,
        }),
      );
      return;
    } else {
      dispatch(login());
    }
  }, [authState.idToken]);

  useEffect(() => {
    if (authState.loggedIn) {
      // First try to get the saved path from sessionStorage
      const savedPath = sessionStorage.getItem('ink_ai_auth_redirect_path');
      // If a saved path exists, use it and clear the storage
      if (savedPath) {
        sessionStorage.removeItem('ink_ai_auth_redirect_path');
        navigate(savedPath, { replace: true });
      } else {
        // Otherwise use the original logic
        const from = location.state?.from?.pathname || '/';
        navigate(from, { replace: true });
      }
    }
  }, [authState.loggedIn]);

  return (
    <Box
      sx={{
        ...bgGradient({
          color: alpha(theme.palette.background.default, 0.9),
          imgUrl: '/images/overlay_4.jpg',
        }),
        height: '100vh',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/images/overlay_4.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.1,
          zIndex: 0,
        }}
      />

      <Logo
        sx={{
          position: 'fixed',
          top: { xs: 16, md: 24 },
          left: { xs: 16, md: 24 },
          zIndex: 1,
        }}
      />

      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: 1, position: 'relative', zIndex: 1 }}
      >
        <Box sx={{ width: '100%', maxWidth: 420, textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 2,
            }}
          >
            <LockIcon sx={{ color: 'primary.main' }} />
          </Box>
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 700, letterSpacing: '-0.5px' }}
          >
            {t('solutionName')}
          </Typography>
        </Box>

        <Card
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 420,
            backdropFilter: 'blur(8px)',
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.95),
            boxShadow: (theme) =>
              `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
          }}
        >
          <Typography variant="h4" align="center" sx={{ mb: 1 }}>
            {t('common.welcome')}
          </Typography>
          <Typography
            variant="body2"
            align="center"
            color="text.secondary"
            sx={{ mb: 4 }}
          >
            {t('common.signInToContinue')}
          </Typography>

          <Box sx={{ position: 'relative', my: 3 }}>
            <Divider>
              <Typography
                variant="caption"
                sx={{ px: 2, textTransform: 'uppercase' }}
              >
                {t('common.secureLogin')}
              </Typography>
            </Divider>
          </Box>

          <LoadingButton
            fullWidth
            size="large"
            variant="contained"
            onClick={onLoginClick}
            loading={authState.loading}
            disabled={!agreed}
          >
            {authState.loading ? t('common.authenticating') : t('common.login')}
          </LoadingButton>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  {t('login.disclaimer')}
                </Typography>
              }
              sx={{
                alignItems: 'flex-start',
                '& .MuiFormControlLabel-label': { lineHeight: 1.4 },
              }}
            />
          </Box>
        </Card>
      </Stack>
    </Box>
  );
};
